import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClientCommand, ServerEvent } from '@leadgab/shared';

/**
 * Realtime connection for the agent app.
 *
 * Reconnects with exponential backoff and jitter. The jitter matters: without
 * it, every agent knocked offline by the same blip comes back in lockstep and
 * hits the server as one wave.
 */

export type ConnectionStatus = 'connecting' | 'open' | 'closed';

interface UseSocketOptions {
  chatUuid: string | null;
  onEvent: (event: ServerEvent) => void;
  enabled?: boolean;
}

interface UseSocketResult {
  status: ConnectionStatus;
  send: (command: ClientCommand) => boolean;
}

const BASE_RETRY_MS = 1000;
const MAX_RETRY_MS = 30_000;

export function useSocket({ chatUuid, onEvent, enabled = true }: UseSocketOptions): UseSocketResult {
  const [status, setStatus] = useState<ConnectionStatus>('closed');
  const socketRef = useRef<WebSocket | null>(null);
  const retryRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const closedByUsRef = useRef(false);

  // Held in a ref so a changing handler identity doesn't tear down the socket.
  const onEventRef = useRef(onEvent);
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const connect = useCallback(() => {
    if (!chatUuid || !enabled) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${protocol}//${window.location.host}/ws?chat=${encodeURIComponent(chatUuid)}`;

    setStatus('connecting');

    const socket = new WebSocket(url);
    socketRef.current = socket;

    socket.onopen = () => {
      retryRef.current = 0;
      setStatus('open');
    };

    socket.onmessage = (event: MessageEvent<string>) => {
      try {
        onEventRef.current(JSON.parse(event.data) as ServerEvent);
      } catch {
        // A malformed frame is not worth tearing the connection down over.
      }
    };

    socket.onclose = () => {
      // Only the socket we are currently holding gets to say the connection is
      // closed, or to ask for another one.
      //
      // `close()` resolves asynchronously, so a socket torn down by an effect
      // re-run reports its close AFTER the replacement has been opened and the
      // "we did this on purpose" flag reset — and then, seeing the flag clear,
      // it scheduled a reconnect of its own. That left two live sockets, and
      // two of every event: the badge counted once but the notification sound
      // played twice, which is how it was noticed.
      if (socketRef.current !== socket) return;

      setStatus('closed');
      socketRef.current = null;

      if (closedByUsRef.current) return;

      const attempt = retryRef.current;
      retryRef.current = attempt + 1;

      const backoff = Math.min(BASE_RETRY_MS * 2 ** attempt, MAX_RETRY_MS);
      const delay = backoff / 2 + Math.random() * (backoff / 2);

      timerRef.current = window.setTimeout(connect, delay);
    };

    socket.onerror = () => {
      socket.close();
    };
  }, [chatUuid, enabled]);

  useEffect(() => {
    closedByUsRef.current = false;
    connect();

    return () => {
      closedByUsRef.current = true;

      if (timerRef.current !== null) window.clearTimeout(timerRef.current);

      const socket = socketRef.current;
      // Cleared before closing, so the close event that arrives later finds it
      // is no longer the current socket and stays quiet.
      socketRef.current = null;
      socket?.close();
    };
  }, [connect]);

  // A tab that slept through a network change reports OPEN on a dead socket;
  // waking up is the moment to check and re-establish.
  useEffect(() => {
    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') return;
      if (socketRef.current?.readyState === WebSocket.OPEN) return;

      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      retryRef.current = 0;
      connect();
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
    };
  }, [connect]);

  const send = useCallback((command: ClientCommand): boolean => {
    const socket = socketRef.current;

    if (socket?.readyState !== WebSocket.OPEN) return false;

    socket.send(JSON.stringify(command));
    return true;
  }, []);

  return { status, send };
}
